import { React, useState } from 'libs/react';
import { useQuery } from '@apollo/client';
import { Button } from 'components/Button';
import { Modal } from 'components/modal/Modal';
import { Spinner } from 'components/Spinner';
import { Icon } from 'components/Icon';
import { EmptyData } from 'components/EmptyData';
import { CheckAvailabilityQuery } from 'graphql/Deal/queries/CheckAvailabilityQuery';
import { GetMewsServiceAvailabilityQuery } from 'graphql/Deal/queries/GetMewsAvailabilityBlocksQuery';
import { GetOperaRoomTypeAvailabilityQuery } from 'graphql/Deal/queries/GetOperaRoomTypeAvailabilityQuery';
import { AccountAssetsQuery } from 'graphql/Account/queries/AccountAssetsQuery';
import { asset } from 'libs/asset';
import { toFormattedDate } from 'components/DateTime';
import { AvailabilityDealList } from '../components/availability/AvailabilityDealList';
import { ExternalAvailabilityGrid } from '../components/availability/ExternalAvailabilityGrid';
import './DealAvailabilityModal.scss';
import { useUser } from 'components/user/UserProvider';
/**
 * @component DealAvailabilityModal
 * @since 1.0.0
 */
export function DealAvailabilityModal(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { deal, onClose, saleItemId, saleItemTitle, isOption } = props;
    const user = useUser();
    const [selectedSaleItemId] = useState(saleItemId);
    // Resolve effective dates: prefer deal.plannedDate (unsaved local value),
    // fall back to deal.plannedDateStart / deal.plannedDateEnd (last saved)
    const plannedDate = deal.plannedDate;
    let effectiveDateStart = null;
    let effectiveDateEnd = null;
    if (plannedDate) {
        if (plannedDate.includes(' - ')) {
            const [start, end] = plannedDate.split(' - ');
            effectiveDateStart = start.split('T')[0];
            effectiveDateEnd = end.split('T')[0];
        }
        else {
            effectiveDateStart = plannedDate.split('T')[0];
        }
    }
    else {
        effectiveDateStart = deal.plannedDateStart
            ? String(deal.plannedDateStart).split('T')[0]
            : null;
        effectiveDateEnd = deal.plannedDateEnd
            ? String(deal.plannedDateEnd).split('T')[0]
            : null;
    }
    const canCheckExternalAvailability = user.can('calendar.externalAvailability.check');
    const { data: accountAssetsData } = useQuery(AccountAssetsQuery, {
        variables: { input: {} },
        fetchPolicy: 'network-only',
        skip: !user.can('asset.manage') && !canCheckExternalAvailability
    });
    const hasMewsAsset = Boolean(canCheckExternalAvailability &&
        ((_a = accountAssetsData === null || accountAssetsData === void 0 ? void 0 : accountAssetsData.accountAssets) === null || _a === void 0 ? void 0 : _a.some((a) => {
            var _a, _b, _c;
            return ((_a = a.asset) === null || _a === void 0 ? void 0 : _a.tag) === 'mews' &&
                ((_b = a.connection) === null || _b === void 0 ? void 0 : _b.active) === true &&
                ((_c = a.connection) === null || _c === void 0 ? void 0 : _c.connected) === true;
        })));
    const hasOperaAsset = Boolean(canCheckExternalAvailability &&
        ((_b = accountAssetsData === null || accountAssetsData === void 0 ? void 0 : accountAssetsData.accountAssets) === null || _b === void 0 ? void 0 : _b.some((a) => {
            var _a, _b, _c;
            return ((_a = a.asset) === null || _a === void 0 ? void 0 : _a.tag) === 'opera' &&
                ((_b = a.connection) === null || _b === void 0 ? void 0 : _b.active) === true &&
                ((_c = a.connection) === null || _c === void 0 ? void 0 : _c.connected) === true;
        })));
    const hasExternalAvailability = hasMewsAsset || hasOperaAsset;
    const canFetchExternalAvailability = Boolean(effectiveDateStart && selectedSaleItemId);
    const { data, loading: loadingAvailability } = useQuery(CheckAvailabilityQuery, {
        variables: {
            input: {
                dateStart: effectiveDateStart,
                dateEnd: effectiveDateEnd,
                saleItemId: selectedSaleItemId !== null && selectedSaleItemId !== void 0 ? selectedSaleItemId : null,
                isOption: isOption !== null && isOption !== void 0 ? isOption : false
            }
        },
        skip: !effectiveDateStart,
        fetchPolicy: 'network-only'
    });
    const { data: mewsData, loading: loadingMews } = useQuery(GetMewsServiceAvailabilityQuery, {
        variables: {
            input: {
                dateStart: effectiveDateStart,
                dateEnd: effectiveDateEnd,
                saleItemId: selectedSaleItemId
            }
        },
        skip: !hasMewsAsset || !canFetchExternalAvailability,
        fetchPolicy: 'network-only'
    });
    const mewsAvailability = mewsData === null || mewsData === void 0 ? void 0 : mewsData.getMewsServiceAvailability;
    const mewsCategories = (_c = mewsAvailability === null || mewsAvailability === void 0 ? void 0 : mewsAvailability.categoryAvailabilities) !== null && _c !== void 0 ? _c : [];
    const mewsTimeUnits = (_d = mewsAvailability === null || mewsAvailability === void 0 ? void 0 : mewsAvailability.timeUnitStartsUtc) !== null && _d !== void 0 ? _d : [];
    const { data: operaData, loading: loadingOpera } = useQuery(GetOperaRoomTypeAvailabilityQuery, {
        variables: {
            input: {
                dateStart: effectiveDateStart,
                dateEnd: effectiveDateEnd,
                saleItemId: selectedSaleItemId
            }
        },
        skip: !hasOperaAsset || !canFetchExternalAvailability,
        fetchPolicy: 'network-only'
    });
    const operaAvailability = operaData === null || operaData === void 0 ? void 0 : operaData.getOperaRoomTypeAvailability;
    const operaCategories = (_e = operaAvailability === null || operaAvailability === void 0 ? void 0 : operaAvailability.categoryAvailabilities) !== null && _e !== void 0 ? _e : [];
    const operaTimeUnits = (_f = operaAvailability === null || operaAvailability === void 0 ? void 0 : operaAvailability.timeUnitStartsUtc) !== null && _f !== void 0 ? _f : [];
    const availability = data === null || data === void 0 ? void 0 : data.checkAvailability;
    const saleItem = (_g = deal.items) === null || _g === void 0 ? void 0 : _g.find((item) => item.item.id === saleItemId);
    const resolvedTitle = saleItemTitle !== null && saleItemTitle !== void 0 ? saleItemTitle : saleItem === null || saleItem === void 0 ? void 0 : saleItem.item.title;
    const footerActions = React.createElement(Button, { label: t('Close'), onPress: onClose });
    const confirmedDeals = (_h = availability === null || availability === void 0 ? void 0 : availability.confirmedDeals) !== null && _h !== void 0 ? _h : [];
    const pendingDeals = (_j = availability === null || availability === void 0 ? void 0 : availability.pendingDeals) !== null && _j !== void 0 ? _j : [];
    const includesCurrentDeal = [...confirmedDeals, ...pendingDeals].some((d) => d.id === deal.id);
    const otherDealsCount = confirmedDeals.length + pendingDeals.length - (includesCurrentDeal ? 1 : 0);
    const formattedDates = effectiveDateStart
        ? effectiveDateEnd && effectiveDateEnd !== effectiveDateStart
            ? `${toFormattedDate(effectiveDateStart, { timeZone: 'UTC' })} → ${toFormattedDate(effectiveDateEnd, { timeZone: 'UTC' })}`
            : toFormattedDate(effectiveDateStart, { timeZone: 'UTC' })
        : null;
    return (React.createElement("div", { className: "deal-availability-modal" },
        React.createElement(Modal, { picto: "calendar-check", title: t('Availability'), onClose: onClose, showFooterAction: true, footerActions: footerActions },
            React.createElement("div", { className: "deal-availability-modal-content" },
                !effectiveDateStart && (React.createElement(EmptyData, { className: "deal-availability-modal-no-date", picto: "calendar", title: t('No planned date'), description: t('Add a planned date to check availability') })),
                effectiveDateStart && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "deal-availability-modal-context" },
                        React.createElement("div", { className: "deal-availability-modal-date" },
                            React.createElement(Icon, { value: "far calendar" }),
                            React.createElement("span", null, formattedDates)),
                        saleItemId && resolvedTitle && (React.createElement("div", { className: "deal-availability-modal-item" }, resolvedTitle))),
                    React.createElement("div", { className: "deal-availability-modal-section-label" },
                        React.createElement("span", null, "Centralio")),
                    loadingAvailability && (React.createElement("div", { className: "deal-availability-modal-loading" },
                        React.createElement(Spinner, { style: "small" }))),
                    !loadingAvailability &&
                        availability &&
                        (otherDealsCount === 0 ? (React.createElement("div", { className: "deal-availability-modal-empty" },
                            React.createElement(Icon, { value: "far circle-check" }),
                            React.createElement("span", null, t('No other quote on these dates')))) : (React.createElement("div", { className: "deal-availability-modal-deals" },
                            React.createElement(AvailabilityDealList, { status: "confirmed", title: t('Confirmed quotes'), deals: confirmedDeals, currentDealId: deal.id }),
                            React.createElement(AvailabilityDealList, { status: "pending", title: t('Pending quotes'), deals: pendingDeals, currentDealId: deal.id })))),
                    hasExternalAvailability && selectedSaleItemId && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "deal-availability-modal-section-label is-external" },
                            React.createElement("span", null,
                                t('External systems'),
                                ' ',
                                React.createElement("span", { className: "beta-badge" }, "Beta"))),
                        hasMewsAsset && (React.createElement(ExternalAvailabilityGrid, { logo: asset('images/assets/mews.jpg'), title: "Mews", loading: loadingMews, loadingMessage: t('Loading Mews availability...'), emptyMessage: t('No availability data found in Mews'), categories: mewsCategories, timeUnits: mewsTimeUnits })),
                        hasOperaAsset && (React.createElement(ExternalAvailabilityGrid, { logo: asset('images/assets/opera.jpg'), title: "Opera Cloud", loading: loadingOpera, loadingMessage: t('Loading Opera Cloud availability...'), emptyMessage: t('No availability data found in Opera Cloud. Map this item to a room type to see inventory.'), categories: operaCategories, timeUnits: operaTimeUnits }))))))))));
}
